/**
 * 8-ball Pool — **rules + move log** (WS-17e). Pure, side-effect-free, and deliberately
 * **physics-free**: this file never imports Planck and never simulates anything. The
 * simulator lives in the web app (`apps/web/src/modules/games/pool/physics.ts`) because
 * only a client ever needs to run it; the server (push) and the replay engine only ever
 * need to know *what happened*, which is a fact recorded in the move log.
 *
 * ## The deterministic-shot architecture (the whole design in one place)
 *
 * A turn is exactly **one** entry in the append-only `Y.Array<PoolMove>` at `GAME_MOVES_KEY`:
 * the shooter, an aim `angle`, a `power`, an optional `cueBallPlacement` (ball-in-hand), and
 * an `outcome` snapshot. Two things are true at once, on purpose:
 *
 *  1. **The inputs alone reproduce the game.** Planck is a fixed-pin dependency, the
 *     simulator uses a fixed timestep and fixed solver iteration counts, and the rack is a
 *     canonical constant (`initialRack()`), so re-running the same `{angle, power, placement}`
 *     sequence from the same start reproduces the same table. That is what makes "replay =
 *     re-simulate" true and what keeps the move log tiny and meaningful.
 *  2. **We do not bet the game on that being true across devices.** The shooter's client
 *     also writes the resulting `outcome` — final ball positions, what got pocketed, which
 *     ball the cue hit first — into the same move entry. Every other client *re-simulates*
 *     the shot for the animation, but when its own result disagrees with the recorded
 *     snapshot it **trusts the snapshot** and logs a `console.warn` (see `replayShot` in
 *     `apps/web/src/modules/games/pool/playback.ts`). Nobody's board can drift from
 *     anybody else's, and we get to measure real-world float drift before believing our own
 *     determinism claim rather than after.
 *
 * Consequence, and the reason this file is shaped the way it is: **`derivePool` reads the
 * snapshot, never the physics.** Given the move log it is a pure, boring state machine that
 * runs identically in a browser, in Node (the push notifier), and in a test — no canvas, no
 * `requestAnimationFrame`, no Planck, no 3 MB of solver on the server. A move with no
 * `outcome` is simply rejected (the same "a stray move never derails the game" rule
 * Connect-4 uses, see `connect4.ts`), because an un-simulated shot has nothing to apply.
 *
 * Fouls are **derived, not stored** — deliberately. A foul is a rules verdict about a
 * physics fact (`pocketed` + `firstContact`), and storing the verdict alongside the fact
 * would create a second authority that could disagree with the first. The snapshot carries
 * only what physics knows; `resolveShot` below is the only thing in the codebase that
 * decides what a foul *is*, so every client (and the server) reaches the same verdict from
 * the same numbers with zero drift surface.
 *
 * ## Rules: basic tier (what's in, what's deliberately out)
 *
 * Implemented: standard 15-ball triangle rack, break, solids/stripes assignment on the
 * first pocketed ball, turn continues on a legal pot, `scratch` / `no contact` /
 * `wrong ball first` fouls with ball-in-hand-anywhere for the opponent, 8-ball last to win,
 * early-8 or 8-with-a-foul to lose.
 *
 * **Not** implemented (basic tier — every one of these is a knowing simplification, not an
 * oversight): no called pockets or called safeties; the table is *not* kept open after the
 * break (a ball pocketed on the break assigns the group, where BCA rules keep it open); no
 * "must drive a ball to a rail" foul; no illegal-break foul; no jumped/off-table balls (the
 * cushions are closed, a ball cannot leave the table); no three-foul rule; no cue-ball
 * placement restricted to the kitchen after a break scratch — ball-in-hand is always
 * anywhere legal; no spin/english (a shot is an angle and a power, nothing else).
 */
/** `0` is the cue ball; `1`–`7` solids, `8` the eight, `9`–`15` stripes. */
export const POOL_CUE_BALL = 0;
export const POOL_EIGHT_BALL = 8;
/**
 * Table geometry, in physics units (metres-ish — chosen so the ball radius sits comfortably
 * above Box2D/Planck's `linearSlop`, which is what keeps contacts from visibly jittering).
 * The proportions are a real 7-foot table's: ~80 ball-radii long, 2:1 playing surface.
 * Rendering scales these to whatever the canvas is; nothing here is pixels.
 */
export const POOL_TABLE_LENGTH = 4;
export const POOL_TABLE_WIDTH = 2;
export const POOL_BALL_RADIUS = 0.05;
/** Generous on purpose — this is a family game, not a simulator. */
export const POOL_POCKET_RADIUS = 0.115;
/** Where the cue ball starts a game (the head spot), and where the rack's apex sits (the
 * foot spot) — quarter and three-quarter of the long axis, as on a real table. */
export const POOL_HEAD_SPOT_X = POOL_TABLE_LENGTH * 0.25;
export const POOL_FOOT_SPOT_X = POOL_TABLE_LENGTH * 0.75;
export const POOL_CENTER_Y = POOL_TABLE_WIDTH / 2;
/** Six pockets: four corners and two sides, centred on the cushion line. */
export const POOL_POCKETS = [
    { x: 0, y: 0 },
    { x: 0, y: POOL_TABLE_WIDTH },
    { x: POOL_TABLE_LENGTH / 2, y: 0 },
    { x: POOL_TABLE_LENGTH / 2, y: POOL_TABLE_WIDTH },
    { x: POOL_TABLE_LENGTH, y: 0 },
    { x: POOL_TABLE_LENGTH, y: POOL_TABLE_WIDTH },
];
/** Storage/comparison precision for snapshot positions — a micrometre on a 4-unit table,
 * far below anything visible, and enough to absorb last-bit float noise inside one engine. */
const QUANTUM = 1e-6;
export function quantize(value) {
    return Math.round(value / QUANTUM) * QUANTUM;
}
/** `1`–`7` are solids, `9`–`15` stripes; the cue ball and the 8 belong to neither. */
export function groupOf(n) {
    if (n >= 1 && n <= 7)
        return 'solids';
    if (n >= 9 && n <= 15)
        return 'stripes';
    return null;
}
export function otherGroup(group) {
    return group === 'solids' ? 'stripes' : 'solids';
}
/**
 * The canonical rack — the constant that makes "same inputs ⇒ same game" possible at all.
 * A standard triangle, apex on the foot spot, 8-ball in the middle of the third row, one
 * solid and one stripe on the back corners:
 *
 * ```
 *          1
 *        9   2
 *     10   8   3
 *   11   4  12   5
 *  6  13   7  14  15
 * ```
 *
 * Rows are spaced by an exact `sqrt(3)/2` of the ball spacing (`Math.sqrt` is
 * IEEE-exact, so this is bit-identical everywhere), with a hair of gap between balls so
 * nothing starts overlapping and gets a solver impulse before anyone has shot.
 */
const RACK_ROWS = [
    [1],
    [9, 2],
    [10, 8, 3],
    [11, 4, 12, 5],
    [6, 13, 7, 14, 15],
];
export function initialRack() {
    const spacing = POOL_BALL_RADIUS * 2 * 1.02;
    const rowGap = spacing * (Math.sqrt(3) / 2);
    const balls = [
        { n: POOL_CUE_BALL, x: quantize(POOL_HEAD_SPOT_X), y: quantize(POOL_CENTER_Y) },
    ];
    RACK_ROWS.forEach((row, rowIndex) => {
        row.forEach((n, i) => {
            balls.push({
                n,
                x: quantize(POOL_FOOT_SPOT_X + rowIndex * rowGap),
                y: quantize(POOL_CENTER_Y + (i - (row.length - 1) / 2) * spacing),
            });
        });
    });
    return sortBalls(balls);
}
/** Canonical ordering — snapshots are compared structurally, so order must never depend on
 * whatever sequence Planck's body list happened to be in. */
export function sortBalls(balls) {
    return [...balls].sort((a, b) => a.n - b.n);
}
/** True once every ball of `group` has been pocketed — i.e. the player is "on the 8". */
export function isOnTheEight(balls, group) {
    if (!group)
        return false;
    return !balls.some((b) => groupOf(b.n) === group);
}
/**
 * The one ball the shooter is allowed to touch first: their own group, or the 8 once
 * they've cleared it, or *any* object ball while the table is still open.
 * Returns `null` for "anything but the 8".
 */
export function legalTargetGroup(state, shooter) {
    const group = state.groups[shooter];
    if (!group)
        return null;
    return isOnTheEight(state.balls, group) ? 'eight' : group;
}
function startingState() {
    return {
        balls: initialRack(),
        pocketed: [],
        groups: [null, null],
        turn: 0,
        ballInHand: false,
        isBreak: true,
        winner: null,
        winReason: null,
        lastShot: null,
        appliedMoves: 0,
        rejectedMoveIndexes: [],
    };
}
function isPlacementLegal(state, at) {
    if (!at)
        return false;
    const r = POOL_BALL_RADIUS;
    if (!Number.isFinite(at.x) || !Number.isFinite(at.y))
        return false;
    if (at.x < r || at.x > POOL_TABLE_LENGTH - r)
        return false;
    if (at.y < r || at.y > POOL_TABLE_WIDTH - r)
        return false;
    // Can't be placed on top of another ball.
    return !state.balls.some((b) => b.n !== POOL_CUE_BALL && Math.hypot(b.x - at.x, b.y - at.y) < r * 2);
}
/**
 * Applies one shot's `outcome` to `state`, returning the next state. The whole rulebook
 * lives here and nowhere else. Pure: `state` is never mutated.
 *
 * Order matters and is the standard one: work out the fouls first (they gate everything),
 * then the 8-ball (it ends the game either way), then group assignment, then whether the
 * shooter keeps the table.
 */
export function resolveShot(state, shooter, outcome) {
    const opponent = shooter === 0 ? 1 : 0;
    const cue = outcome.balls.find((b) => b.n === POOL_CUE_BALL);
    const scratched = outcome.pocketed.includes(POOL_CUE_BALL) || !cue;
    // The cue ball stays on the table unless it dropped — while it's off (ball in hand) the
    // next shooter's placement is what puts it back, so it simply isn't in `balls`.
    const balls = sortBalls(scratched ? outcome.balls.filter((b) => b.n !== POOL_CUE_BALL) : outcome.balls);
    // --- fouls -------------------------------------------------------------------------
    const fouls = [];
    if (scratched)
        fouls.push('scratch');
    const target = legalTargetGroup(state, shooter);
    if (outcome.firstContact === null) {
        fouls.push('no-contact');
    }
    else if (target === 'eight') {
        if (outcome.firstContact !== POOL_EIGHT_BALL)
            fouls.push('wrong-ball-first');
    }
    else if (target === null) {
        // Open table: any object ball is fair game, the 8 is not.
        if (outcome.firstContact === POOL_EIGHT_BALL)
            fouls.push('wrong-ball-first');
    }
    else if (groupOf(outcome.firstContact) !== target) {
        fouls.push('wrong-ball-first');
    }
    const potted = outcome.pocketed.filter((n) => n !== POOL_CUE_BALL);
    const pocketed = [...state.pocketed, ...outcome.pocketed];
    // --- the 8-ball ends the game, one way or the other ----------------------------------
    if (potted.includes(POOL_EIGHT_BALL)) {
        const wasOnTheEight = isOnTheEight(state.balls, state.groups[shooter]);
        const won = wasOnTheEight && fouls.length === 0;
        return {
            ...state,
            balls,
            pocketed,
            turn: null,
            ballInHand: false,
            isBreak: false,
            winner: won ? shooter : opponent,
            winReason: won ? 'eight-ball' : 'opponent-lost-eight',
            lastShot: { shooter, pocketed: outcome.pocketed, fouls, continued: false, assigned: null },
            appliedMoves: state.appliedMoves + 1,
        };
    }
    // --- group assignment ----------------------------------------------------------------
    // Basic tier: the *first* ball to drop on a clean shot decides, break included (BCA keeps
    // the table open after the break; we don't — see the file docblock).
    let groups = state.groups;
    let assigned = null;
    if (!state.groups[shooter] && fouls.length === 0) {
        const firstGroup = potted.map(groupOf).find((g) => g !== null);
        if (firstGroup) {
            assigned = firstGroup;
            groups =
                shooter === 0 ? [firstGroup, otherGroup(firstGroup)] : [otherGroup(firstGroup), firstGroup];
        }
    }
    // --- who shoots next -----------------------------------------------------------------
    const myGroup = groups[shooter];
    const pottedMine = myGroup
        ? potted.some((n) => groupOf(n) === myGroup)
        : potted.some((n) => groupOf(n) !== null);
    const continued = fouls.length === 0 && pottedMine;
    return {
        ...state,
        balls,
        pocketed,
        groups,
        turn: continued ? shooter : opponent,
        // Every foul hands over ball-in-hand-anywhere (BCA standard; see the docblock for the
        // kitchen restriction we deliberately don't implement).
        ballInHand: fouls.length > 0,
        isBreak: false,
        lastShot: { shooter, pocketed: outcome.pocketed, fouls, continued, assigned },
        appliedMoves: state.appliedMoves + 1,
    };
}
/**
 * Replays a pool move log into a state — the `derive` this game kind plugs into the generic
 * replay engine (`replay.ts`), and the same "the move log IS the game" contract Connect-4
 * has. Every move is re-validated at derive time (right shooter, game not over, snapshot
 * present, legal placement); an invalid one is skipped, never fatal, so a CRDT merge race
 * or a client that wrote garbage costs at most that one entry.
 */
export function derivePool(moves, playerIds) {
    let state = startingState();
    const rejectedMoveIndexes = [];
    for (let i = 0; i < moves.length; i += 1) {
        const move = moves[i];
        const turn = state.turn;
        if (!move ||
            turn === null ||
            move.shooterId !== playerIds[turn] ||
            !move.outcome ||
            !Array.isArray(move.outcome.balls) ||
            !Array.isArray(move.outcome.pocketed) ||
            !Number.isFinite(move.angle) ||
            !Number.isFinite(move.power)) {
            rejectedMoveIndexes.push(i);
            continue;
        }
        if (state.ballInHand && !isPlacementLegal(state, move.cueBallPlacement)) {
            rejectedMoveIndexes.push(i);
            continue;
        }
        state = resolveShot(state, turn, move.outcome);
    }
    return { ...state, rejectedMoveIndexes };
}
/**
 * The exact ball layout a shot starts from — the cue ball placed if the shooter had
 * ball-in-hand, otherwise the table as the previous shot left it. Shared by the simulator
 * (so it re-simulates from the same start the shooter did) and the aiming UI (so the guide
 * line is drawn against the balls the shot will actually meet).
 */
export function shotStartBalls(state, placement) {
    const balls = state.balls.filter((b) => b.n !== POOL_CUE_BALL);
    const cueAt = state.ballInHand
        ? placement
        : (state.balls.find((b) => b.n === POOL_CUE_BALL) ?? {
            x: POOL_HEAD_SPOT_X,
            y: POOL_CENTER_Y,
        });
    if (cueAt)
        balls.push({ n: POOL_CUE_BALL, x: cueAt.x, y: cueAt.y });
    return sortBalls(balls);
}
/** Structural equality of two snapshots, to `tolerance` on every position. The divergence
 * check that decides whether a re-simulating client trusts itself or the shooter. */
export function outcomesMatch(a, b, tolerance = 1e-4) {
    if (a.firstContact !== b.firstContact)
        return false;
    if (a.pocketed.length !== b.pocketed.length)
        return false;
    if (a.pocketed.some((n, i) => n !== b.pocketed[i]))
        return false;
    if (a.balls.length !== b.balls.length)
        return false;
    const left = sortBalls(a.balls);
    const right = sortBalls(b.balls);
    return left.every((ball, i) => {
        const other = right[i];
        if (!other || other.n !== ball.n)
            return false;
        return Math.abs(other.x - ball.x) <= tolerance && Math.abs(other.y - ball.y) <= tolerance;
    });
}
/** Plain-language line for the UI, e.g. "Scratch — ball in hand" / "Potted the 4". Kept
 * here so the board, the replay scrubber and any future spectator view all say the same
 * thing. Facts only, never a taunt or a score (`memory/no-demand-mechanics.md`). */
export function describeShot(shot) {
    if (!shot)
        return '';
    if (shot.fouls.includes('scratch'))
        return 'Scratch — ball in hand';
    if (shot.fouls.includes('no-contact'))
        return 'Hit nothing — ball in hand';
    if (shot.fouls.includes('wrong-ball-first'))
        return 'Wrong ball first — ball in hand';
    const potted = shot.pocketed.filter((n) => n !== POOL_CUE_BALL);
    if (potted.length === 0)
        return 'No pot';
    if (potted.length === 1)
        return `Potted the ${potted[0]}`;
    return `Potted the ${potted.slice(0, -1).join(', ')} and ${potted[potted.length - 1]}`;
}
//# sourceMappingURL=pool.js.map